import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Sparkle, Robot, CheckCircle, XCircle, Warning, ArrowRight,
  EnvelopeSimple, Trophy, Share, Copy, DownloadSimple, MapPin, ChartLineUp,
  Lightning, Calendar,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import SEOHead from "@/lib/seo/SEOHead";
import { trackMetaEvent, trackMetaStandardEvent } from "@/lib/metaPixel";
import GbpCandidatePicker from "../components/GbpCandidatePicker";
import ManualVerificationModal from "../components/ManualVerificationModal";
import {
  GooglePositionCard as _GooglePositionCard,
  MeasurementOriginNote as _MeasurementOriginNote,
} from "./visibility/GooglePositionCard";
import DiscoveryReport from "./visibility/DiscoveryReport";
import AnswerVisibilityBlock from "./visibility/AnswerVisibilityBlock";
import BlueprintMeetingCTA from "./visibility/BlueprintMeetingCTA";

const SHARE_BASE = API.replace(/\/api$/, "");

// Version identifier for the SMS consent copy stored with every opted-in lead.
// Bump this whenever the consent language on the page changes so downstream
// compliance audits can reproduce exactly what the user agreed to.
const SMS_CONSENT_TEXT_VERSION = "ranktrust_sms_consent_v8_2026_08_voluntary";

const ENGINE_META = {
  chatgpt: { label: "ChatGPT", color: "#10A37F" },
  claude: { label: "Claude", color: "#D97706" },
  perplexity: { label: "Perplexity", color: "#20808D" },
};

// ---------- Sub-components (small, focused, per-section) ----------

function BusinessIdentityPanel({ result, onChooseAnotherListing, chooseAnotherRef }) {
  const verified = result.verification_status === "verified";
  const unable = result.verification_status === "unable_to_verify";
  if (!verified && !unable) return null;

  const displayName = result.gbp_business_name || result.business_name;
  const gbpCity = result.gbp_city;
  const gbpState = result.gbp_state;
  const marketCity = result.city;
  const marketState = result.state;
  const marketDiffers =
    gbpCity && gbpState &&
    (gbpCity.toLowerCase() !== (marketCity || "").toLowerCase() ||
      gbpState.toLowerCase() !== (marketState || "").toLowerCase());

  if (unable) {
    return (
      <section
        className="bento p-5 lg:p-6 border border-zinc-200 bg-zinc-50/60"
        data-testid="business-identity-panel"
      >
        <div className="label-tiny mb-3 text-zinc-500">Business Identity</div>
        <div className="flex items-start gap-2" data-testid="business-identity-unable-to-verify">
          <span
            className="mt-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-500 text-white text-[10px] font-bold"
            aria-hidden="true"
          >!</span>
          <p className="font-display text-base lg:text-lg font-bold text-zinc-900 leading-snug">
            Unable to Verify
          </p>
        </div>
        <p className="text-sm text-zinc-600 mt-2 leading-relaxed">
          See the verification guidance below for how to confirm your listing.
        </p>
      </section>
    );
  }

  // Verified
  return (
    <section
      className="bento p-5 lg:p-6 border border-zinc-200"
      data-testid="business-identity-panel"
    >
      <div className="label-tiny mb-4 text-zinc-500">Business Identity</div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
        <div>
          <dt className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">Business Name</dt>
          <dd className="font-display text-base font-bold text-zinc-900 mt-1" data-testid="identity-business-name">
            {displayName}
          </dd>
        </div>

        {(gbpCity || gbpState) && (
          <div>
            <dt className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">
              Google Business Profile Location
            </dt>
            <dd className="text-sm font-semibold text-zinc-900 mt-1" data-testid="identity-gbp-location">
              {[gbpCity, gbpState].filter(Boolean).join(", ")}
            </dd>
          </div>
        )}

        {result.gbp_primary_category && (
          <div>
            <dt className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">Primary Category</dt>
            <dd className="text-sm font-semibold text-zinc-900 mt-1" data-testid="identity-primary-category">
              {result.gbp_primary_category}
            </dd>
          </div>
        )}

        {(marketCity || marketState) && (
          <div>
            <dt className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">Assessment Market</dt>
            <dd className="text-sm font-semibold text-zinc-900 mt-1" data-testid="identity-assessment-market">
              {[marketCity, marketState].filter(Boolean).join(", ")}
            </dd>
          </div>
        )}

        {result.keyword && (
          <div>
            <dt className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">Search Keyword</dt>
            <dd className="text-sm font-semibold text-zinc-900 mt-1" data-testid="identity-search-keyword">
              {result.keyword}
            </dd>
          </div>
        )}

        <div>
          <dt className="text-[11px] font-mono uppercase tracking-wider text-zinc-500">Verification Status</dt>
          <dd className="text-sm font-semibold text-emerald-700 mt-1 flex items-center gap-1.5" data-testid="identity-verification-status">
            <span
              className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-600 text-white text-[10px] font-bold"
              aria-hidden="true"
            >
              &#10003;
            </span>
            Verified Google Business Profile
          </dd>
        </div>
      </dl>

      {marketDiffers && (
        <p
          className="text-xs text-zinc-600 mt-4 pt-4 border-t border-zinc-200 leading-relaxed"
          data-testid="identity-market-mismatch-note"
        >
          Your Google Business Profile is registered in {gbpCity}, {gbpState}, but
          this assessment measures rankings for the market you serve —{" "}
          {[marketCity, marketState].filter(Boolean).join(", ")}. That&apos;s why
          the rankings below reflect a different city than your office address.
        </p>
      )}

      {onChooseAnotherListing && (
        <div className={`mt-4 pt-4 ${marketDiffers ? "" : "border-t border-zinc-200"}`}>
          <button
            type="button"
            ref={chooseAnotherRef}
            onClick={onChooseAnotherListing}
            className="text-xs text-zinc-600 hover:text-zinc-900 underline underline-offset-2 decoration-zinc-300 hover:decoration-zinc-900 transition focus:outline-none focus:ring-2 focus:ring-[#2D5BFF] focus:ring-offset-2 rounded"
            data-testid="identity-choose-another-listing"
            aria-label="Not your business? Choose a different Google listing"
          >
            Not your business? Choose a different listing.
          </button>
        </div>
      )}
    </section>
  );
}

function UnableToVerifyBanner({
  result,
  onCorrectInfo, onSearchListing, onManualVerify, onCannotFind,
  actionsDisabled = false,
  showNotFoundState = false,
}) {
  if (result.verification_status !== "unable_to_verify") return null;
  if (showNotFoundState) {
    return (
      <section
        className="bento p-6 lg:p-7 border-l-4 border-amber-500 bg-amber-50/60"
        data-testid="unable-to-verify-banner"
      >
        <div className="label-tiny mb-2 text-amber-700">We Couldn&apos;t Find Your Listing</div>
        <p className="font-display text-lg lg:text-xl font-bold text-zinc-900 leading-snug" data-testid="identity-not-found-headline">
          We couldn&apos;t confidently identify your Google Business Profile.
        </p>
        <p className="text-sm text-zinc-700 mt-3 leading-relaxed" data-testid="identity-not-found-body">
          This may mean your listing doesn&apos;t exist yet, is claimed under a
          different name, or hasn&apos;t been verified with Google. Until we can
          confirm your identity, we won&apos;t report position, review count,
          or projected revenue — those numbers would be unreliable.
        </p>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="unable-to-verify-not-found-actions">
          <button
            type="button" onClick={onCorrectInfo} disabled={actionsDisabled}
            className="text-xs font-mono uppercase tracking-wider px-3 py-2 rounded-md border border-zinc-300 hover:border-zinc-900 hover:bg-white transition disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-amber-500"
            data-testid="uv-action-correct-info-notfound"
          >
            Correct my information
          </button>
          <button
            type="button" onClick={onSearchListing} disabled={actionsDisabled}
            className="text-xs font-mono uppercase tracking-wider px-3 py-2 rounded-md border border-zinc-300 hover:border-zinc-900 hover:bg-white transition disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-amber-500"
            data-testid="uv-action-search-listing-notfound"
          >
            Try search again
          </button>
        </div>
      </section>
    );
  }
  return (
    <section
      className="bento p-6 lg:p-7 border-l-4 border-amber-500 bg-amber-50/60"
      data-testid="unable-to-verify-banner"
    >
      <div className="label-tiny mb-2 text-amber-700">Unable to Verify</div>
      <p className="font-display text-lg lg:text-xl font-bold text-zinc-900 leading-snug" data-testid="unable-to-verify-headline">
        We couldn&apos;t confidently match your Google Business Profile from the top-40 results.
      </p>
      <p className="text-sm text-zinc-700 mt-3 leading-relaxed" data-testid="unable-to-verify-body">
        Confirm your business name and the market you serve, then re-run this
        assessment against the correct listing. Until your GBP is verified,
        we won&apos;t report position, review count, or projected revenue —
        those numbers would be unreliable.
      </p>
      <div
        className="mt-5 pt-4 border-t border-amber-200 grid grid-cols-1 sm:grid-cols-2 gap-2"
        role="group"
        aria-label="Recovery actions"
        data-testid="unable-to-verify-actions"
      >
        <button
          type="button" onClick={onCorrectInfo} disabled={actionsDisabled}
          className="text-left text-sm px-3 py-2.5 rounded-md border border-zinc-300 bg-white hover:border-zinc-900 hover:bg-zinc-50 transition disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-amber-500"
          data-testid="uv-action-correct-info"
        >
          <div className="font-bold text-zinc-900">Correct my information</div>
          <div className="text-xs text-zinc-500 mt-0.5">Fix a typo without starting over</div>
        </button>
        <button
          type="button" onClick={onSearchListing} disabled={actionsDisabled}
          className="text-left text-sm px-3 py-2.5 rounded-md border border-zinc-300 bg-white hover:border-zinc-900 hover:bg-zinc-50 transition disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-amber-500"
          data-testid="uv-action-search-listing"
        >
          <div className="font-bold text-zinc-900">Search for my listing</div>
          <div className="text-xs text-zinc-500 mt-0.5">Show me broader Google results</div>
        </button>
        <button
          type="button" onClick={onManualVerify} disabled={actionsDisabled}
          className="text-left text-sm px-3 py-2.5 rounded-md border border-zinc-300 bg-white hover:border-zinc-900 hover:bg-zinc-50 transition disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-amber-500"
          data-testid="uv-action-manual-verify"
        >
          <div className="font-bold text-zinc-900">Verify with a Google listing link</div>
          <div className="text-xs text-zinc-500 mt-0.5">Paste your Google Maps URL or Place ID</div>
        </button>
        <button
          type="button" onClick={onCannotFind} disabled={actionsDisabled}
          className="text-left text-sm px-3 py-2.5 rounded-md border border-zinc-300 bg-white hover:border-zinc-900 hover:bg-zinc-50 transition disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-amber-500"
          data-testid="uv-action-cannot-find"
        >
          <div className="font-bold text-zinc-900">I cannot find my listing</div>
          <div className="text-xs text-zinc-500 mt-0.5">Continue with limited data</div>
        </button>
      </div>
    </section>
  );
}

function DiagnosisHook({ result }) {
  const hi = result.highest_impact;
  const rank = [result.map_position, result.map_rank, result.current_rank].find((value) => typeof value === "number");
  const verified = result.verification_status === "verified";
  const market = [result.city, result.state].filter(Boolean).join(", ");
  const keyword = result.keyword ? `“${result.keyword}”` : "this search";
  const hasVerifiedMapOpportunity = verified && !result.serpapi_unavailable && (typeof rank === "number" || result.canonical_only);

  let hook = hi?.hook || "";
  if (hasVerifiedMapOpportunity) {
    if (result.canonical_only || result.map_matched === false) {
      hook = `Your Google Business Profile is live, but your business is not appearing in the top 40 Google Maps results for ${keyword}${market ? ` in ${market}` : ""}.`;
    } else if (rank > 3) {
      hook = `RankTrust measured your business at #${rank} for ${keyword}${market ? ` in ${market}` : ""}. Customers can encounter multiple competitors before reaching your business.`;
    }
  }

  if (!hook) return null;
  return (
    <section className="bento p-6 lg:p-8 border-l-4 border-[#2D5BFF]" data-testid="diagnosis-hook">
      <div className="label-tiny mb-2 text-[#2D5BFF]">Highest-Impact Opportunity Detected</div>
      <p className="font-display text-2xl lg:text-3xl font-black tracking-tight leading-tight text-zinc-900" data-testid="diagnosis-hook-sentence">
        {hook}
      </p>
      <div className="text-xs font-mono text-zinc-500 mt-4">Here is the evidence RankTrust found…</div>
    </section>
  );
}

function MeasurementOriginNote(props) {
  return <_MeasurementOriginNote {...props} />;
}

function GooglePositionCard(props) {
  return <_GooglePositionCard {...props} />;
}

function ContributingFactorsBlock({ factors, hasPositionAbove }) {
  if (!factors || factors.length === 0) return null;
  return (
    <section data-testid="contributing-factors-block">
      {hasPositionAbove && (
        <p className="text-sm text-zinc-500 italic mb-2" data-testid="factors-bridge">
          Here are the strongest observed signals behind this opportunity.
        </p>
      )}
      <div className="label-tiny mb-3">Observed Opportunity Signals</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {factors.map((f, i) => {
          const websiteAccessIncomplete = f.factor === "website_unreachable";
          const headline = websiteAccessIncomplete
            ? "Website analysis could not be completed reliably."
            : f.headline;
          const evidence = websiteAccessIncomplete
            ? "RankTrust could not reliably access the website for automated analysis during this run. This does not necessarily mean the customer-facing website is unavailable."
            : f.evidence;
          const action = websiteAccessIncomplete
            ? "Recheck the website signal before making a recommendation."
            : f.action;
          return (
            <div key={i} className="bento p-5" data-testid={`contributing-factor-${i}`}>
              <div className="font-display text-base font-bold text-zinc-900 leading-snug">
                {headline}
              </div>
              {evidence && (
                <div className="text-xs font-mono text-zinc-500 mt-2" data-testid={`contributing-evidence-${i}`}>
                  {evidence}
                </div>
              )}
              {action && (
                <div className="text-xs text-zinc-700 mt-3 leading-relaxed">
                  <span className="font-bold uppercase tracking-wider text-[10px] text-zinc-500">Do this: </span>
                  {action}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function CompetitorsBlock({ top3, businessName }) {
  if (!top3 || top3.length === 0) return null;
  return (
    <section className="bento p-6" data-testid="competitors-block">
      <h2 className="font-display text-xl font-bold tracking-tight flex items-center gap-2 mb-1">
        <Trophy size={18} weight="fill" className="text-[#EF4444]" /> Competitive Visibility Snapshot
      </h2>
      <p className="text-sm text-zinc-600 mb-4">
        These are three businesses currently receiving stronger Google visibility for this search.
      </p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b border-zinc-200 text-[11px] uppercase tracking-wider text-zinc-500 font-mono">
              <th className="py-2 pr-4">#</th>
              <th className="py-2 pr-4">Business</th>
              <th className="py-2 pr-4 text-right">Rating</th>
              <th className="py-2 pl-4 text-right">Reviews</th>
            </tr>
          </thead>
          <tbody>
            {top3.map((c, i) => (
              <tr key={i} className="border-b border-zinc-100 last:border-0" data-testid={`competitor-row-${i}`}>
                <td className="py-3 pr-4 font-mono text-zinc-500 tabular-nums">{c.rank ?? "—"}</td>
                <td className="py-3 pr-4 font-display font-bold text-zinc-900">{c.name}</td>
                <td className="py-3 pr-4 text-right font-mono tabular-nums">
                  {c.rating != null ? `${c.rating}★` : "—"}
                </td>
                <td className="py-3 pl-4 text-right font-mono tabular-nums">
                  {c.review_count != null ? c.review_count.toLocaleString() : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AiVisibilityBlock({ result }) {
  const engines = result.engines || {};
  const providerOrder = [
    ["chatgpt", "ChatGPT"], ["perplexity", "Perplexity"], ["claude", "Claude"],
    ["gemini", "Gemini"], ["google_ai", "Google AI"], ["meta_ai", "Meta AI"], ["grok", "Grok"],
  ];
  const measured = providerOrder.filter(([key]) => engines[key] && !engines[key].mocked);
  const measuredRates = measured.map(([key]) => Number(engines[key]?.mention_rate)).filter(Number.isFinite);
  const combined = measuredRates.length ? Math.round(measuredRates.reduce((a, b) => a + b, 0) / measuredRates.length) : null;
  const measuredLabels = measured.map(([key, label]) => label);
  const measuredBasis = measuredLabels.length === 1 ? `Based on ${measuredLabels[0]} only in this assessment.` : measuredLabels.length > 1 ? `Based on ${measuredLabels.join(", ")} in this assessment.` : "No AI provider returned a measured result in this assessment.";
  return (
    <section className="bento p-6" data-testid="ai-visibility-block">
      <h2 className="font-display text-xl font-bold tracking-tight flex items-center gap-2 mb-1"><Robot size={18} weight="fill" /> AI Search Visibility</h2>
      <p className="text-sm text-zinc-600 mb-4">When customers use AI to research who to hire, RankTrust measures where provider data is available and clearly identifies what has not yet been measured.</p>
      <div className="mb-5"><div className="text-xs font-mono text-zinc-500">AI discovery sources measured</div><div className="font-display text-2xl font-black tabular-nums text-zinc-900 mt-1">{measured.length} of {providerOrder.length}</div><div className="text-xs font-mono text-zinc-500 mt-4">Measured AI mention rate</div><div className="font-display text-4xl font-black tabular-nums text-zinc-900">{combined == null ? "—" : combined}<span className="text-zinc-500 text-xl">{combined == null ? "" : "%"}</span></div><p className="text-xs font-semibold text-zinc-700 mt-1">{measuredBasis}</p><p className="text-xs text-zinc-500 mt-1">Unavailable or not-yet-integrated providers do not count as zero.</p></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {providerOrder.map(([key, label]) => {
          const data = engines[key];
          const meta = ENGINE_META[key] || { label, color: "#52525B" };
          const measuredNow = data && !data.mocked;
          const unavailable = data && data.mocked;
          return <div key={key} className="bento p-4" style={{ borderLeft: `3px solid ${meta.color}` }} data-testid={`engine-status-${key}`}>
            <div className="flex items-baseline justify-between gap-3"><span className="label-tiny" style={{ color: meta.color }}>{label}</span>{measuredNow && <span className="font-display text-2xl font-black tabular-nums">{data.mention_rate}<span className="text-zinc-500 text-sm">%</span></span>}</div>
            <div className="font-mono text-xs text-zinc-500 mt-2">{measuredNow ? (data.mentioned_count > 0 ? `Cited in ${data.mentioned_count} of ${data.total} test prompts` : `Not yet appearing in ${data.total} test prompts`) : unavailable ? "Data unavailable from this provider right now." : "Not yet measured in this assessment."}</div>
          </div>;
        })}
      </div>
    </section>
  );
}

function OverallScoreBlock({ result }) {
  const legacyScore = result.local_visibility_score;
  const breakdown = result.visibility_factor_breakdown;
  const summary = result.score_summary;
  if (legacyScore == null || !breakdown) return null;
  const answer = result?.answer_visibility || {};
  const readiness = answer?.answer_readiness || {};
  const strongAnswers = Number(readiness?.strong_answers || 0);
  const weakAnswers = Number(readiness?.weak_answers || 0);
  const missingAnswers = Number(readiness?.missing_answers || 0);
  const answerTotal = strongAnswers + weakAnswers + missingAnswers;
  const legacyWebsitePts = Number(breakdown.website_pts || 0);
  const legacyWebsiteMax = Number(breakdown.website_max || 15);
  let websitePts = legacyWebsitePts;
  let websiteNote = "Based on observable website health signals.";
  if (answer?.analysis_status === "complete" && answerTotal > 0) {
    const readinessRatio = (strongAnswers + (weakAnswers * 0.5)) / answerTotal;
    const healthRatio = legacyWebsiteMax ? legacyWebsitePts / legacyWebsiteMax : 0;
    websitePts = Math.max(0, Math.min(legacyWebsiteMax, Math.round(legacyWebsiteMax * ((healthRatio * 0.35) + (readinessRatio * 0.65)))));
    websiteNote = `Website health plus Answer Visibility: ${strongAnswers} strong, ${weakAnswers} needs work, ${missingAnswers} missing.`;
  }
  const score = Math.max(0, Math.min(100, Math.round(Number(legacyScore) - legacyWebsitePts + websitePts)));
  const rows = [
    { label: "Discovery & Visibility", pts: breakdown.rank_pts, max: breakdown.rank_max },
    { label: "Trust & Customer Confidence", pts: breakdown.trust_pts, max: breakdown.trust_max },
    { label: "Website & Answer Intelligence", pts: websitePts, max: legacyWebsiteMax, note: websiteNote },
    { label: "Business Profile Signals", pts: breakdown.profile_pts, max: breakdown.profile_max },
    { label: "AI Visibility", pts: breakdown.ai_pts, max: breakdown.ai_max },
  ];
  return (<section className="bento p-6 lg:p-8" data-testid="overall-visibility-score-block">
    <p className="text-sm text-zinc-500 italic mb-3">Here's what those externally observable signals add up to.</p>
    {summary && <p className="font-display text-lg lg:text-xl font-bold tracking-tight text-zinc-900 leading-snug mb-5">{summary}</p>}
    <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-3 mb-4"><div><div className="label-tiny text-[#2D5BFF] mb-2">Directional Intelligence</div><h2 className="font-display text-2xl font-bold tracking-tight">Business Opportunity & Rank Signal</h2><p className="text-sm text-zinc-600 mt-1 max-w-xl">A directional measure of the externally observable signals RankTrust assessed — not a grade on the quality of your business.</p></div><div className="font-display text-6xl lg:text-7xl font-black tabular-nums text-zinc-900">{score}<span className="text-zinc-400 text-3xl">/100</span></div></div>
    <div className="space-y-3">{rows.map((r, i) => { const pct = r.max ? Math.max(0, Math.min(100, (r.pts / r.max) * 100)) : 0; return <div key={i}><div className="flex justify-between text-xs font-mono text-zinc-600 mb-1"><span>{r.label}</span><span className="tabular-nums">{r.pts} / {r.max}</span></div><div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden"><div className="h-full bg-[#2D5BFF]" style={{ width: `${pct}%` }} /></div>{r.note && <div className="text-[11px] text-zinc-500 mt-1.5">{r.note}</div>}</div>; })}</div>
  </section>);
}

function GrowthPlanBlock({ plan }) {
  if (!plan) return null;
  const cols = [
    { key: "30_days", label: "Address First", icon: Lightning, items: plan["30_days"] },
    { key: "60_days", label: "Build Next", icon: ChartLineUp, items: plan["60_days"] },
    { key: "90_days", label: "Strengthen Over Time", icon: Calendar, items: plan["90_days"] },
  ];
  const hasAny = cols.some((c) => c.items && c.items.length > 0);
  if (!hasAny) return null;
  return (
    <section className="bento p-6 lg:p-8" data-testid="growth-plan-block">
      <h2 className="font-display text-2xl font-bold tracking-tight mb-1">
        Your 30 / 60 / 90-day Growth Plan
      </h2>
      <p className="text-sm text-zinc-600 mb-5">
        AI-generated recommendations based only on the signals above. Review with your team before executing.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cols.map((c, i) => {
          const Icon = c.icon;
          return (
            <div key={c.key} className="border border-zinc-200 p-4" data-testid={`plan-col-${i}`}>
              <div className="flex items-center gap-2 mb-3 text-[#2D5BFF]">
                <Icon size={16} weight="bold" />
                <span className="label-tiny">{c.label}</span>
              </div>
              <ul className="space-y-2 text-sm text-zinc-800 leading-snug">
                {(c.items || []).map((it, j) => (
                  <li key={j} className="flex gap-2" data-testid={`plan-item-${i}-${j}`}>
                    <span className="text-zinc-400 mt-1">•</span>
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Verdict({ rate }) {
  let label, color;
  if (rate >= 60) { label = "Strong"; color = "#22C55E"; }
  else if (rate >= 30) { label = "Emerging"; color = "#EAB308"; }
  else { label = "Invisible"; color = "#EF4444"; }
  return <span className="font-display font-bold" style={{ color }}>{label}</span>;
}

function OpportunityActionHorizon({ result }) {
  const answer = result?.answer_visibility || {};
  const context = result?.pre_diagnosis_answers || {};
  const top3 = Array.isArray(result?.top3_competitors) ? result.top3_competitors : [];
  const mapRank = Number(result?.map_position || 0);
  const aiRate = Number(result?.ai_mention_rate ?? 0);
  const readiness = answer?.answer_readiness || {};
  const missingAnswers = Number(readiness?.missing_answers || 0);
  const weakAnswers = Number(readiness?.weak_answers || 0);
  const measuredAi = Object.entries(result?.engines || {}).filter(([, data]) => data && !data.mocked && typeof data.mention_rate === "number");
  const measuredAiRates = measuredAi.map(([, data]) => Number(data.mention_rate)).filter(Number.isFinite);
  const measuredAiRate = measuredAiRates.length ? Math.round((measuredAiRates.reduce((sum, value) => sum + value, 0) / measuredAiRates.length) * 10) / 10 : null;
  const engineName = (name) => String(name || "").toLowerCase() === "chatgpt" ? "ChatGPT" : String(name || "").replace(/^./, (ch) => ch.toUpperCase());
  const priorityItems = [];
  const addPriority = (item) => { if (item && item.severity > 0) priorityItems.push(item); };
  if (mapRank > 3) addPriority({ key: "google", dimension: "Google Visibility", title: "Earn consideration earlier in the customer journey", evidenceLabel: "RankTrust observed", evidence: `For this measured search, the business appears at #${mapRank}. Customers can encounter multiple competitors before reaching this position.`, implication: "The opportunity is broader than a ranking change. It is to increase the number of relevant moments where the business can enter a customer's consideration set.", question: "Where else could a ready-to-buy customer discover you before they decide who belongs on their shortlist?", severity: mapRank <= 10 ? 1 : 3, observed: true });
  if (measuredAiRate != null) {
    const providers = measuredAi.map(([name, data]) => `${engineName(name)} ${data.mention_rate}%`).join(" · ");
    addPriority({ key: "ai", dimension: "AI Answers", title: measuredAiRate === 0 ? "Build visibility where AI recommendations are forming" : "Strengthen AI recommendation visibility", evidenceLabel: "RankTrust observed", evidence: measuredAi.length === 1 ? `Your business was not named in any of the ${engineName(measuredAi[0][0])} recommendation tests completed for this assessment. Measured provider: ${providers}.` : `Across the AI providers measured in this assessment, your business was named ${measuredAiRate}% of the time. Provider breakdown: ${providers}.`, implication: "Customers increasingly use AI systems to narrow their options before visiting a website or making contact. The opportunity is to make the business easier for measured answer engines to understand and include.", question: "What would an AI system need to clearly understand before it could confidently recommend this business?", severity: measuredAiRate < 30 ? 3 : measuredAiRate < 60 ? 2 : 0, observed: true });
  }
  if (context.lead_response) addPriority({ key: "lead-response", dimension: "Lead Response", title: "Protect the first customer conversation", evidenceLabel: "What you told us", evidence: context.lead_response === "Yes, instantly" ? "You told us new inquiries are answered almost immediately." : context.lead_response === "Usually within an hour" ? "You told us new inquiries are usually answered within an hour." : "You told us new inquiries can take hours or days to receive a response.", implication: context.lead_response === "Yes, instantly" ? "That response speed is a strength worth protecting as lead volume grows." : "In urgent home-service situations, customers often keep searching until someone responds. A faster-responding competitor can win the conversation first.", question: "What would it take to make the first real response consistently immediate?", severity: context.lead_response === "Yes, instantly" ? 0 : context.lead_response === "Usually within an hour" ? 2 : 4, observed: false });
  if (context.estimate_follow_up) addPriority({ key: "estimate-follow-up", dimension: "Estimate Follow-Up", title: "Keep qualified opportunities from going quiet", evidenceLabel: "What you told us", evidence: context.estimate_follow_up === "Yes, automated/tracked" ? "You told us estimates are consistently tracked and followed up." : context.estimate_follow_up === "Sometimes, manually" ? "You told us estimates are followed up manually and inconsistently." : "You told us estimates are not consistently followed up.", implication: context.estimate_follow_up === "Yes, automated/tracked" ? "That process is a strength because interested prospects are less likely to disappear simply because follow-up was missed." : "Interested prospects can go quiet simply because no structured follow-up happens at the right time.", question: "How many estimates could be recovered with a consistent follow-up process?", severity: context.estimate_follow_up === "Yes, automated/tracked" ? 0 : context.estimate_follow_up === "Sometimes, manually" ? 2 : 4, observed: false });
  if (context.reviews_trust) addPriority({ key: "reviews-context", dimension: "Reviews & Trust Context", title: "Use existing trust more deliberately", evidenceLabel: "What you told us", evidence: `You told us: ${context.reviews_trust}.`, implication: "The opportunity may be less about raw review volume and more about how existing trust is used throughout the customer journey.", question: "Where could strong public trust be made more visible before a customer decides?", severity: context.reviews_trust === "Yes, we dominate" ? 0 : context.reviews_trust === "They help, but we need more" ? 2 : 3, observed: false });
  priorityItems.sort((a, b) => (b.severity - a.severity) || (Number(b.observed) - Number(a.observed)));
  const fallback = { dimension: "Deeper Discovery", title: "Continue business opportunity discovery", evidenceLabel: "Requires deeper discovery", evidence: "Public evidence and the answers provided have reached their current limit.", implication: "The next opportunity may sit inside the business and should be validated rather than assumed.", question: "Where could opportunity be slipping away after a customer responds?", severity: 1, observed: false };
  const horizonItems = priorityItems.slice(0, 3);
  if (horizonItems.length === 2) horizonItems.push(fallback);
  const horizonLabels = ["Address First", "Build Next", "Strengthen Over Time"];
  const cards = horizonItems.map((item, index) => ({ label: horizonLabels[index], item }));
  const operatingSignals = [
    context.lead_response ? { label: "Lead Response", answer: context.lead_response, detail: context.lead_response === "Yes, instantly" ? "You told us new inquiries are answered almost immediately. That response speed is a strength worth protecting as lead volume grows." : context.lead_response === "Usually within an hour" ? "You told us new inquiries are usually answered within an hour. In urgent home-service situations, customers often keep searching until someone responds. Even a few minutes can give another contractor the opportunity to answer first and win the conversation." : "You told us new inquiries can take hours or days to receive a response. In urgent home-service situations, that creates a substantial opportunity for a faster-responding competitor to win the conversation first." } : null,
    context.estimate_follow_up ? { label: "Estimate Follow-Up", answer: context.estimate_follow_up, detail: context.estimate_follow_up === "Yes, automated/tracked" ? "You told us estimates are consistently tracked and followed up. That process is a strength because interested prospects are less likely to disappear simply because follow-up was missed." : context.estimate_follow_up === "Sometimes, manually" ? "You told us estimates are followed up manually and inconsistently. That creates a risk that interested prospects go quiet simply because no structured follow-up happens at the right time." : "You told us estimates are not consistently followed up. That creates a clear risk that qualified opportunities are lost after the estimate simply because the next conversation never happens." } : null,
  ].filter(Boolean);
  const unansweredOperatingQuestions = [
    "What happens to customers after the original job is complete?",
    "Which repetitive processes consume time without creating customer value?",
  ];
  return (<section data-testid="opportunity-action-horizon" className="space-y-5"><div data-testid="opportunity-action-horizon-heading"><div className="label-tiny mb-2 text-[#2D5BFF]">RankTrustOS Prioritization</div><h2 className="font-display text-2xl lg:text-3xl font-black tracking-tight text-zinc-900">Opportunity Action Horizon</h2><p className="text-sm text-zinc-600 mt-2 leading-relaxed max-w-3xl">Not every opportunity deserves attention at the same time. RankTrustOS connects what customers can discover, what builds confidence and what removes uncertainty — then separates what the evidence supports from what still needs to be learned.</p></div><div className="grid grid-cols-1 md:grid-cols-3 gap-4">{cards.map(({ label, item }, index) => (<article key={label} className="bento p-5 lg:p-6 border border-zinc-200 bg-white"><div className="flex items-center justify-between gap-3"><div className="text-[11px] font-mono uppercase tracking-wider text-[#2D5BFF]">{label}</div><div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">0{index + 1}</div></div><div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 mt-4">{item.dimension}</div><h3 className="font-display text-lg font-bold text-zinc-900 mt-1 leading-snug">{item.title}</h3><div className="mt-4"><div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">{item.evidenceLabel || "What we can see"}</div><p className="text-sm text-zinc-600 mt-1.5 leading-relaxed">{item.evidence}</p></div><div className="mt-4"><div className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">Why it may matter</div><p className="text-sm text-zinc-600 mt-1.5 leading-relaxed">{item.implication}</p></div><div className="mt-5 pt-4 border-t border-zinc-200"><div className="text-[10px] font-mono uppercase tracking-wider text-[#A97800]">Think about this</div><p className="text-sm font-semibold text-zinc-800 mt-1.5 leading-relaxed">{item.question}</p></div></article>))}</div><div className="bento p-5 lg:p-7 border border-zinc-200 bg-zinc-50" data-testid="deeper-discovery-opportunities"><div className="label-tiny text-zinc-500">Operating Context · What You Told Us</div><h3 className="font-display text-xl lg:text-2xl font-black text-zinc-900 mt-2">The public assessment ends where the business opportunity conversation begins.</h3><p className="text-sm text-zinc-600 mt-2 max-w-3xl leading-relaxed">RankTrust keeps self-reported operating context separate from public evidence. Where you already answered a discovery question, we carry that answer forward instead of asking it again.</p>{operatingSignals.length > 0 && <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">{operatingSignals.map((signal) => <div key={signal.label} className="rounded-lg bg-white border border-zinc-200 p-4"><div className="text-[10px] font-mono uppercase tracking-wider text-[#2D5BFF]">What you told us · {signal.label}</div><div className="text-sm font-bold text-zinc-900 mt-1.5">{signal.answer}</div><p className="text-sm text-zinc-600 mt-2 leading-relaxed">{signal.detail}</p></div>)}</div>}<div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5">{unansweredOperatingQuestions.map((question) => <div key={question} className="rounded-lg bg-white border border-zinc-200 p-4"><div className="text-[10px] font-mono uppercase tracking-wider text-[#A97800]">Still worth discovering</div><p className="text-sm font-semibold text-zinc-800 mt-1.5">{question}</p></div>)}</div></div></section>);
}

// ---------- Main page ----------

export default function PublicVisibilityChecker() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ business_name: "", keyword: "", city: "", state: "", email: "", lead_phone: "" });
  const [showContactFields, setShowContactFields] = useState(false);
  useEffect(() => {
    const contactReady = Boolean(
      (form.business_name || "").trim() &&
      (form.keyword || "").trim() &&
      (form.city || "").trim() &&
      (form.state || "").trim()
    );
    if (!contactReady) {
      setShowContactFields(false);
      return;
    }
    const timer = setTimeout(() => setShowContactFields(true), 800);
    return () => clearTimeout(timer);
  }, [form.business_name, form.keyword, form.city, form.state]);

  const [smsConsent, setSmsConsent] = useState(false);
  const [smsConsentError, setSmsConsentError] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [email, setEmail] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  // SMS follow-up
  const [smsPhone, setSmsPhone] = useState("");
  const [smsSending, setSmsSending] = useState(false);
  const [smsQueued, setSmsQueued] = useState(false);
  const [smsDeliveryStatus, setSmsDeliveryStatus] = useState(null);
  const [smsMocked, setSmsMocked] = useState(false);
  const [smsError, setSmsError] = useState("");
  const [smsErrorCode, setSmsErrorCode] = useState(null);

  // Phase 2 Slice 2A — GBP candidate picker state.
  const [candidatePickerOpen, setCandidatePickerOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [candidateCacheId, setCandidateCacheId] = useState(null);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [pickerSubmitting, setPickerSubmitting] = useState(false);
  // Ref to the "Not your business?" trigger so focus returns to it when the
  // picker closes (accessibility requirement — chief-architect A11y spec).
  const chooseAnotherRef = useRef(null);
  // Flag distinguishing an initial-submit picker from a mid-report correction
  // picker so analytics can attribute correction requests correctly.
  const [pickerIsCorrection, setPickerIsCorrection] = useState(false);
  // Slice 2E — Unable-to-Verify recovery actions state
  const [manualVerifyOpen, setManualVerifyOpen] = useState(false);
  const [showNotFoundState, setShowNotFoundState] = useState(false);
  const [manualVerifying, setManualVerifying] = useState(false);
  const formSectionRef = useRef(null);

  // Deep-link: ?cid=... loads an existing check
  useEffect(() => {
    const cid = searchParams.get("cid");
    if (cid && !result) {
      axios.get(`${API}/public/visibility-check/${cid}`)
        .then(({ data }) => setResult(data))
        .catch(() => { /* ignore */ });
    }
  }, [result, searchParams]);

  const shareUrl = result ? `${SHARE_BASE}/api/share/check/${result.id}` : "";
  const ogImageUrl = result ? `${SHARE_BASE}/api/public/visibility-check/${result.id}/og.png` : "";

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied — paste it on LinkedIn or X");
    } catch {
      toast.error("Copy failed — long-press the link to copy manually");
    }
  };

  const tweetText = result
    ? `I just ran a Business Growth Assessment for ${result.business_name} in ${result.city}. Run yours free 👇`
    : "";
  const twitterShareHref = result
    ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}&url=${encodeURIComponent(shareUrl)}`
    : "#";
  const linkedinShareHref = result
    ? `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`
    : "#";

  // SMS delivery status polling
  useEffect(() => {
    if (!smsQueued || !result?.id || smsMocked) return;
    if (smsDeliveryStatus === "delivered" || smsDeliveryStatus === "failed" || smsDeliveryStatus === "undelivered") return;
    let attempts = 0;
    const maxAttempts = 10;
    const iv = setInterval(async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/public/visibility-check/${result.id}/sms-status`);
        if (data.status) setSmsDeliveryStatus(data.status);
        if (data.error_message) setSmsError(data.error_message);
        if (data.error_code != null) setSmsErrorCode(data.error_code);
        if (data.is_final || attempts >= maxAttempts) clearInterval(iv);
      } catch (_e) {
        if (attempts >= maxAttempts) clearInterval(iv);
      }
    }, 3000);
    return () => clearInterval(iv);
  }, [smsQueued, smsDeliveryStatus, smsMocked, result?.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.keyword || !form.city) {
      toast.error("Business name, keyword, and city are required");
      return;
    }
    const preEmail = (form.email || "").trim();
    const prePhone = (form.lead_phone || "").trim();
    const validEmail = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(preEmail);
    const phoneDigits = prePhone.replace(/\D/g, "");
    const validPhone = phoneDigits.length === 10 || (phoneDigits.length === 11 && phoneDigits.startsWith("1"));
    if (!validEmail && !validPhone) {
      toast.error("Enter a valid email address or mobile number to continue");
      return;
    }
    // Twilio A2P 10DLC: SMS consent is voluntary. The Business Growth
    // Assessment must complete whether or not a phone number is entered
    // and whether or not the SMS consent box is checked. Consent is
    // recorded only when the visitor voluntarily checks the box.
    const phoneEntered = (form.lead_phone || "").trim().length > 0;
    const consentGiven = phoneEntered && smsConsent === true;
    setSmsConsentError(false);
    trackMetaEvent("AssessmentStarted", {
      content_name: "Business Growth Assessment",
      source: "ranktrust_check",
    });
    trackMetaStandardEvent("ViewContent", {
      content_name: "Business Growth Assessment Started",
      content_category: "assessment",
    });
    try {
      if (window.posthog && typeof window.posthog.capture === "function") {
        window.posthog.capture("assessment_started", { source: "ranktrust_check" });
      }
    } catch (_) {}
    setRunning(true);
    setResult(null);
    setSmsQueued(false);
    setSmsDeliveryStatus(null);
    setSmsError("");
    setSmsErrorCode(null);
    // Affirmative consent metadata is stamped ONLY when both the phone
    // number is provided AND the visitor voluntarily checked the box.
    const payload = {
      ...form,
      sms_consent_given: consentGiven,
      sms_consent_text_version: consentGiven ? SMS_CONSENT_TEXT_VERSION : null,
      sms_consent_timestamp: consentGiven ? new Date().toISOString() : null,
      sms_consent_method: consentGiven ? "checked_checkbox" : null,
      sms_consent_source: consentGiven ? "ranktrust_check" : null,
    };
    // Phase 2 Slice 2A — pre-flight GBP candidate resolution.
    // If the provider returns multiple credible matches, we prompt the
    // visitor to confirm which listing is theirs BEFORE the assessment
    // runs. Any provider failure or single-match response falls through
    // silently to the normal flow.
    try {
      const { data: cand } = await axios.post(`${API}/public/gbp-candidates`, {
        business_name: form.business_name, city: form.city, state: form.state,
        keyword: form.keyword,
      });
      if (cand && cand.requires_picker && Array.isArray(cand.candidates) && cand.candidates.length > 0) {
        setCandidates(cand.candidates);
        setCandidateCacheId(cand.cache_id || null);
        setPendingPayload({ ...payload, identity_cache_id: cand.cache_id || null });
        setPickerIsCorrection(false);
        setCandidatePickerOpen(true);
        trackIdentityEvent("candidate_picker_shown", {
          candidate_count: cand.candidates.length,
          top_confidence: cand.top_confidence || null,
          cache_id: cand.cache_id || null,
        });
        setRunning(false);
        return;
      }
      // Auto-select the single HIGH-confidence match when present.
      if (cand && !cand.requires_picker && Array.isArray(cand.candidates) && cand.candidates.length > 0) {
        payload.confirmed_place_id = cand.candidates[0].place_id;
        payload.identity_cache_id = cand.cache_id || null;
      }
    } catch (err) {
      // Non-blocking — the visibility-check endpoint runs its own
      // identity resolution as a fallback.
      console.warn("gbp-candidates pre-flight failed:", err?.message);
    }

    await runVisibilityCheck(payload);
  };

  // Phase 2 Slice 2A — lightweight analytics sink. Never blocks the visitor.
  const trackIdentityEvent = (event, extras = {}) => {
    try {
      // Fire-and-forget; failures are logged server-side only.
      axios.post(`${API}/public/identity-events`, {
        event,
        cache_id: candidateCacheId,
        check_id: (extras.check_id !== undefined ? extras.check_id : (result?.id || null)),
        ...extras,
      }).catch(() => {});
    } catch (_) { /* never block on analytics */ }
  };

  const runVisibilityCheck = async (payload) => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/public/visibility-check`, payload);
      let finalData = data;
      const submittedEmail = (payload.email || "").trim();
      const submittedPhone = (payload.lead_phone || "").trim();
      if (data?.id && (submittedEmail || submittedPhone)) {
        try {
          const { data: unlocked } = await axios.post(`${API}/public/visibility-check/${data.id}/unlock`, {
            email: submittedEmail || null,
            phone: submittedPhone || null,
            sms_consent_given: payload.sms_consent_given === true,
            sms_consent_timestamp: payload.sms_consent_timestamp || null,
            sms_consent_text_version: payload.sms_consent_text_version || null,
            sms_consent_method: payload.sms_consent_method || null,
            sms_consent_source: payload.sms_consent_source || null,
          });
          finalData = unlocked || data;
        } catch (notifyErr) {
          console.error("assessment email notification failed:", notifyErr?.response?.data || notifyErr?.message);
        }
      }
      setResult(finalData);
      trackMetaEvent("AssessmentCompleted", {
        content_name: "Business Growth Assessment",
        source: "ranktrust_check",
        check_id: finalData?.id || data?.id || undefined,
      });
      trackMetaStandardEvent("Lead", {
        content_name: "Business Growth Assessment Completed",
        content_category: "assessment",
      });
      try {
        if (window.posthog && typeof window.posthog.capture === "function") {
          window.posthog.capture("assessment_completed", {
            source: "ranktrust_check",
            check_id: finalData?.id || data?.id || null,
          });
        }
      } catch (_) {}
      if (finalData.sms_status === "sent") {
        setSmsQueued(true);
        if (finalData.sms_mocked) {
          setSmsMocked(true);
          setSmsDeliveryStatus("delivered");
        } else {
          setSmsDeliveryStatus("queued");
        }
      } else if (finalData.sms_status === "failed") {
        setSmsError("We were unable to deliver the text.");
        if (finalData.sms_error_code != null) setSmsErrorCode(finalData.sms_error_code);
      }
      return finalData;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Check failed — please try again");
      return null;
    } finally {
      setRunning(false);
    }
  };

  const handlePickerSelect = async (placeId) => {
    if (!pendingPayload || pickerSubmitting) return;
    setPickerSubmitting(true);
    const finalPayload = { ...pendingPayload, confirmed_place_id: placeId };
    const wasCorrection = pickerIsCorrection;
    const originalConfidence = pickerIsCorrection ? (result?.identity_confidence || null) : null;
    const originalSource = pickerIsCorrection ? (result?.identity_source || null) : null;
    setCandidatePickerOpen(false);
    setPendingPayload(null);
    setPickerIsCorrection(false);
    trackIdentityEvent("candidate_selected", {
      candidate_count: candidates.length,
      corrected_candidate_selected: wasCorrection,
    });
    // Preserve scroll position for the correction flow so the visitor's
    // context doesn't jump on re-render.
    const scrollY = wasCorrection ? window.scrollY : null;
    const updated = await runVisibilityCheck(finalPayload);
    setPickerSubmitting(false);
    if (wasCorrection && updated) {
      trackIdentityEvent("identity_corrected", {
        original_identity_confidence: originalConfidence,
        original_identity_source: originalSource,
        final_identity_confidence: updated.identity_confidence || null,
        assessment_completed_after_correction: true,
        check_id: updated.id || null,
      });
      // Restore scroll on the next paint so the identity panel stays in view.
      if (scrollY != null) {
        requestAnimationFrame(() => window.scrollTo({ top: scrollY, behavior: "instant" }));
      }
      // Return focus to the trigger button when the picker closes.
      if (chooseAnotherRef.current && typeof chooseAnotherRef.current.focus === "function") {
        requestAnimationFrame(() => chooseAnotherRef.current && chooseAnotherRef.current.focus());
      }
    }
  };

  const handlePickerNoneMatch = async () => {
    if (!pendingPayload || pickerSubmitting) return;
    setPickerSubmitting(true);
    const finalPayload = { ...pendingPayload };
    delete finalPayload.confirmed_place_id;
    setCandidatePickerOpen(false);
    setPendingPayload(null);
    setPickerIsCorrection(false);
    trackIdentityEvent("no_candidate_selected", {
      candidate_count: candidates.length,
    });
    await runVisibilityCheck(finalPayload);
    setPickerSubmitting(false);
  };

  const handlePickerCancel = () => {
    if (pickerSubmitting) return;
    setCandidatePickerOpen(false);
    setCandidates([]);
    setPendingPayload(null);
    setPickerIsCorrection(false);
    setRunning(false);
    // Return focus to the correction trigger if this was a mid-report picker.
    if (chooseAnotherRef.current && typeof chooseAnotherRef.current.focus === "function") {
      requestAnimationFrame(() => chooseAnotherRef.current && chooseAnotherRef.current.focus());
    }
  };

  const handleChooseAnotherListing = async () => {
    if (candidatePickerOpen || running || pickerSubmitting) return;
    if (!form.business_name || !form.city || !form.state) return;
    // Compose the payload we'd submit as if the visitor re-ran the assessment
    // from the existing form values (form state is preserved across the flow).
    const payload = {
      ...form,
      sms_consent_given: false,
      sms_consent_text_version: null,
      sms_consent_timestamp: null,
      sms_consent_method: null,
      sms_consent_source: null,
    };
    trackIdentityEvent("identity_correction_requested", {
      original_identity_confidence: result?.identity_confidence || null,
      original_identity_source: result?.identity_source || null,
      check_id: result?.id || null,
    });
    try {
      const { data: cand } = await axios.post(`${API}/public/gbp-candidates`, {
        business_name: form.business_name, city: form.city, state: form.state,
        keyword: form.keyword,
      });
      const list = Array.isArray(cand?.candidates) ? cand.candidates : [];
      if (list.length === 0) {
        toast.error(
          cand?.source_status === "unavailable"
            ? "Google listings are temporarily unavailable — please try again in a moment."
            : "No alternative listings found for this business."
        );
        return;
      }
      setCandidates(list);
      setCandidateCacheId(cand.cache_id || null);
      setPendingPayload({ ...payload, identity_cache_id: cand.cache_id || null });
      setPickerIsCorrection(true);
      setCandidatePickerOpen(true);
      trackIdentityEvent("candidate_picker_shown", {
        candidate_count: list.length,
        top_confidence: cand.top_confidence || null,
      });
    } catch (err) {
      toast.error("Unable to load listings right now — please try again.");
    }
  };

  // ------ Slice 2E — Unable-to-Verify recovery actions ------

  const handleCorrectMyInformation = () => {
    trackIdentityEvent("correct_my_information_requested", {});
    // Clear the current result (returns visitor to the form in place) but
    // preserve the entered form values so they can edit + resubmit.
    setResult(null);
    setSmsQueued(false);
    setSmsDeliveryStatus(null);
    setSmsError("");
    setSmsErrorCode(null);
    setShowNotFoundState(false);
    // Scroll the form into view; keep scroll behavior smooth for UX.
    requestAnimationFrame(() => {
      if (formSectionRef.current && typeof formSectionRef.current.scrollIntoView === "function") {
        formSectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    });
  };

  const handleSearchForMyListing = async () => {
    if (candidatePickerOpen || running || pickerSubmitting) return;
    if (!form.business_name || !form.city || !form.state) {
      toast.error("Please enter your business name, city, and state first.");
      return;
    }
    trackIdentityEvent("search_for_my_listing_requested", {});
    try {
      const { data: cand } = await axios.post(`${API}/public/gbp-candidates`, {
        business_name: form.business_name, city: form.city, state: form.state,
        keyword: form.keyword, broader: true,
      });
      const list = Array.isArray(cand?.candidates) ? cand.candidates : [];
      if (list.length === 0) {
        const unavailable = cand?.source_status === "unavailable";
        toast.error(
          unavailable
            ? "Google listings are temporarily unavailable — please try again in a moment."
            : "We still couldn't find your listing. Try correcting your business name or city."
        );
        return;
      }
      const payload = { ...form,
        sms_consent_given: false, sms_consent_text_version: null,
        sms_consent_timestamp: null, sms_consent_method: null, sms_consent_source: null,
      };
      setCandidates(list);
      setCandidateCacheId(cand.cache_id || null);
      setPendingPayload({ ...payload, identity_cache_id: cand.cache_id || null, correction_method: "search_broader" });
      setPickerIsCorrection(true);
      setCandidatePickerOpen(true);
      setShowNotFoundState(false);
      trackIdentityEvent("candidate_picker_shown", {
        candidate_count: list.length,
        top_confidence: cand.top_confidence || null,
        cache_id: cand.cache_id || null,
      });
    } catch (err) {
      toast.error("Unable to load listings right now — please try again.");
    }
  };

  const handleManualVerify = () => {
    trackIdentityEvent("manual_verification_attempted", {});
    setManualVerifyOpen(true);
  };

  const handleManualResolve = async (rawInput) => {
    if (manualVerifying) return;
    setManualVerifying(true);
    try {
      const { data } = await axios.post(`${API}/public/resolve-identity`, {
        input: rawInput,
        business_name: form.business_name || null,
        city: form.city || null,
        state: form.state || null,
      });
      if (!data || !data.success) {
        trackIdentityEvent("manual_verification_failed", {
          payload: { reason: data?.reason || "unknown" },
        });
        toast.error(data?.message || "We couldn't verify that listing.");
        return;
      }
      const placeId = data.place_id;
      const method = data.verification_method || "manual_url";
      const previousPid = result?.map_place_id || null;
      setManualVerifyOpen(false);
      const payload = { ...form,
        sms_consent_given: false, sms_consent_text_version: null,
        sms_consent_timestamp: null, sms_consent_method: null, sms_consent_source: null,
        confirmed_place_id: placeId,
        correction_method: method,
        previous_place_id: previousPid,
      };
      const updated = await runVisibilityCheck(payload);
      if (updated) {
        trackIdentityEvent("manual_verification_succeeded", {
          check_id: updated.id || null,
          final_identity_confidence: updated.identity_confidence || null,
          corrected_candidate_selected: true,
          assessment_completed_after_correction: true,
        });
        trackIdentityEvent("assessment_restarted_after_correction", {
          check_id: updated.id || null,
        });
      }
    } catch (err) {
      trackIdentityEvent("manual_verification_failed", {
        payload: { reason: "network_error" },
      });
      toast.error("Unable to verify that listing right now — please try again.");
    } finally {
      setManualVerifying(false);
    }
  };

  const handleCannotFindListing = () => {
    trackIdentityEvent("identity_not_found", {
      original_identity_source: result?.identity_source || null,
    });
    setShowNotFoundState(true);
  };

  const sendDiagnosisSms = async (phoneOverride) => {
    const phone = (phoneOverride ?? smsPhone).trim();
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 10 || digits.length > 11) {
      toast.error("Enter a valid 10-digit US mobile number");
      return;
    }
    if (!result?.id) return;
    setSmsSending(true);
    setSmsError("");
    setSmsErrorCode(null);
    setSmsDeliveryStatus(null);
    try {
      const { data } = await axios.post(`${API}/public/visibility-check/${result.id}/send-sms`, { phone });
      if (data.ok) {
        setSmsQueued(true);
        setSmsMocked(!!data.mocked);
        setSmsDeliveryStatus(data.mocked ? "delivered" : "queued");
        toast.success(data.mocked ? "Your diagnosis has been delivered to your mobile device." : "Your text is being sent…");
      } else {
        setSmsError(data.reason || "We were unable to deliver the text.");
        if (data.error_code != null) setSmsErrorCode(data.error_code);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "We were unable to deliver the text.";
      setSmsError(msg);
      const ec = err?.response?.data?.error_code;
      if (ec != null) setSmsErrorCode(ec);
    }
    setSmsSending(false);
  };

  const captureEmail = async (e) => {
    e.preventDefault();
    if (!email.match(/^[^@\s]+@[^@\s]+\.[^@\s]+$/)) {
      toast.error("Enter a valid email");
      return;
    }
    try {
      await axios.post(`${API}/public/visibility-check/capture-email`, {
        check_id: result.id,
        email,
      });
      setEmailSubmitted(true);
      toast.success("Full report unlocked — taking you to the next step");
      setTimeout(() => {
        navigate(`/presell/video-1?cid=${result.id}&email=${encodeURIComponent(email)}`);
      }, 600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Email capture failed");
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <SEOHead path="/check" />
      {/* Header */}
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="block">
            <img
              src="https://customer-assets.emergentagent.com/job_gbp-top-3/artifacts/mlg09j9p_Ranktrust%20new%20logo.png"
              alt="RankTrust"
              style={{ height: 36, width: "auto", display: "block" }}
            />
            <div className="hidden sm:block" style={{ fontSize: 10, letterSpacing: "1.5px", color: "#6B7280", fontWeight: 600, textTransform: "uppercase", marginTop: 4, whiteSpace: "nowrap" }}>
              The Business Growth Intelligence Platform
            </div>
          </Link>
          {!result ? (
            <Link to="/register" className="btn-dark text-[11px] px-2.5 py-2 sm:text-sm sm:px-3 whitespace-nowrap shrink-0" data-testid="header-start-trial">
              <span className="hidden sm:inline">Start free trial</span>
              <span className="sm:hidden">Start trial</span>
              <ArrowRight size={14} weight="bold" />
            </Link>
          ) : (
            <div className="text-xs font-mono text-zinc-500">No account required</div>
          )}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
        {/* Hero — shown only pre-result */}
        {!result && (
          <div className="mb-10">
            <div className="label-tiny mb-2 flex items-center gap-2">
              <Robot size={14} weight="bold" /> No account required · ~15 seconds
            </div>
            <h1 className="font-display text-5xl lg:text-6xl font-black tracking-tighter leading-[0.9]" data-testid="checker-title">
              Get Your <span className="bg-[#2D5BFF] text-white px-2">Business Growth Assessment</span>
            </h1>
            <p className="text-zinc-600 mt-4 max-w-2xl text-base lg:text-lg">
              Discover where your business may be missing visibility, customer confidence and growth opportunities across Google, your website, AI answers and the competitive journey. Free.
            </p>
          </div>
        )}

        {/* Form */}
        {!result && (
          <form ref={formSectionRef} onSubmit={submit} className="bento p-6 lg:p-8 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl" data-testid="checker-form">
            <div className="md:col-span-2">
              <label className="label-tiny mb-1 block">Your business name</label>
              <input
                className="input-field w-full"
                placeholder="e.g. Joe Plumbing"
                value={form.business_name}
                onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                data-testid="checker-business-name"
                required
              />
            </div>
            <div>
              <label className="label-tiny mb-1 block">Service / Keyword</label>
              <input
                className="input-field w-full"
                placeholder="e.g. emergency plumber"
                value={form.keyword}
                onChange={(e) => setForm({ ...form, keyword: e.target.value })}
                data-testid="checker-keyword"
                required
              />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <label className="label-tiny mb-1 block">City</label>
                <input
                  className="input-field w-full"
                  placeholder="Austin"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  data-testid="checker-city"
                  required
                />
              </div>
              <div>
                <label className="label-tiny mb-1 block">State</label>
                <input
                  className="input-field w-full"
                  placeholder="TX"
                  maxLength={3}
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })}
                  data-testid="checker-state"
                />
              </div>
            </div>
            {showContactFields && (
              <div className="contents" data-testid="preassessment-contact-reveal">
            <div className="md:col-span-2">
              <label className="label-tiny mb-1 block">Email address <span className="text-zinc-400 font-normal">(email or mobile required)</span></label>
              <input
                type="email"
                autoComplete="email"
                className="input-field w-full"
                placeholder="you@business.com"
                value={form.email || ""}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="checker-email"
              />
            </div>
            <div className="md:col-span-2">
              <label className="label-tiny mb-1 block">Mobile number <span className="text-zinc-400 font-normal">(optional)</span></label>
              <input
                type="tel"
                autoComplete="tel"
                inputMode="tel"
                className="input-field w-full"
                placeholder="(817) 555-1234"
                value={form.lead_phone}
                onChange={(e) => {
                  setForm({ ...form, lead_phone: e.target.value });
                  // Reset consent state when phone is cleared
                  if (!e.target.value.trim()) {
                    setSmsConsent(false);
                    setSmsConsentError(false);
                  }
                }}
                data-testid="checker-phone"
              />

              {/* Unified SMS consent block — the purpose statement AND the
                  full opt-in language sit together in ONE visible box next
                  to the checkbox. No separate helper paragraph, no
                  dropdown, no hidden text. All language visible on initial
                  page load so Twilio A2P reviewers and carriers can see
                  the exact consent context and opt-in wording without any
                  interaction. Unchecked by default. */}
              <div className="mt-3 rounded-lg border border-zinc-200 bg-zinc-50 p-4" data-testid="sms-consent-block">
                <p className="text-xs leading-relaxed text-zinc-700 mb-3" data-testid="checker-phone-helper">
                  Enter your mobile number if you&apos;d like to receive your Business Growth Assessment, assessment results, visibility reports, appointment scheduling, account updates, and related customer service communications by text.
                </p>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={smsConsent}
                    onChange={(e) => {
                      setSmsConsent(e.target.checked);
                      if (e.target.checked) setSmsConsentError(false);
                    }}
                    className="mt-1 w-4 h-4 rounded border-zinc-300 text-[#2D5BFF] focus:ring-[#2D5BFF] shrink-0"
                    data-testid="sms-consent-checkbox"
                    aria-required="true"
                  />
                  <span className="text-xs leading-relaxed text-zinc-700" data-testid="sms-consent-text">
                    By checking this box, you agree to receive recurring SMS messages from Card Service Warehouse, Inc. (DBA: RankTrust) regarding your Business Growth Assessment, assessment results, visibility reports, appointment scheduling, account updates, and related customer service communications. Message frequency varies but will not exceed 5 messages per month. Message and data rates may apply. Reply STOP to opt out or HELP for help. Consent is voluntary and is not a condition of purchase. View our{" "}
                    <a href="/terms" target="_blank" rel="noreferrer" className="underline text-zinc-900 hover:text-[#2D5BFF]" data-testid="sms-consent-terms-link">Terms of Service</a>
                    {" "}and{" "}
                    <a href="/privacy" target="_blank" rel="noreferrer" className="underline text-zinc-900 hover:text-[#2D5BFF]" data-testid="sms-consent-privacy-link">Privacy Policy</a>.
                  </span>
                </label>
                {smsConsentError && (
                  <p className="mt-2 text-xs font-semibold" style={{ color: "#DC2626" }} data-testid="sms-consent-error">
                    Please provide SMS consent to receive your Growth Diagnosis by text, or remove the phone number to continue without SMS.
                  </p>
                )}
              </div>
            </div>
            <div className="md:col-span-2 mt-2">
              <button type="submit" className="btn-primary w-full md:w-auto" disabled={running} data-testid="checker-run">
                {running ? (
                  <><span className="spinner"></span> Analyzing visibility… ~15s</>
                ) : (
                  <><Sparkle size={14} weight="fill" /> Run Free Business Growth Assessment</>
                )}
              </button>
              <p className="text-xs text-zinc-500 font-mono mt-3">
                Free to use. No account required. Limited to 5 reports per day.
              </p>
              <p className="text-xs text-zinc-600 mt-3 leading-relaxed" data-testid="checker-tos-disclosure">
                By continuing, you agree to our{" "}
                <a href="/terms" target="_blank" rel="noreferrer" className="underline text-zinc-900 hover:text-[#2D5BFF]" data-testid="checker-terms-link">Terms of Service</a>
                {" "}and{" "}
                <a href="/privacy" target="_blank" rel="noreferrer" className="underline text-zinc-900 hover:text-[#2D5BFF]" data-testid="checker-privacy-link">Privacy Policy</a>
                .
              </p>
            </div>
              </div>
            )}
          </form>
        )}

        {/* Results — new spec v2.1 ordering */}
        {result && result.discovery_view && (
          <div className="space-y-6" data-testid="discovery-report-shell">
            {/* Business identity line — quiet header consistent with full view */}
            <div className="text-sm text-zinc-500 font-mono" data-testid="business-identity-line">
              Analysis complete for{" "}
              <span className="font-display font-bold text-zinc-900">
                {result.business_name}
              </span>
              {" — "}
              {result.keyword} · {result.city}, {result.state}
            </div>
            <DiscoveryReport result={result} />
          </div>
        )}

        {result && !result.discovery_view && (
          <div className="space-y-6">
            {/* 1. Business identity line — quiet */}
            <div className="text-sm text-zinc-500 font-mono" data-testid="business-identity-line">
              Analysis complete for <span className="font-display font-bold text-zinc-900">{result.business_name}</span> — {result.keyword} · {result.city}, {result.state}
            </div>

            {/* 1.25 Business Identity Panel — makes canonical GBP resolution visible */}
            <BusinessIdentityPanel
              result={result}
              onChooseAnotherListing={handleChooseAnotherListing}
              chooseAnotherRef={chooseAnotherRef}
            />

            {/* 1.5 Unable-to-verify banner — shown ONLY when identity match failed */}
            <UnableToVerifyBanner
              result={result}
              onCorrectInfo={handleCorrectMyInformation}
              onSearchListing={handleSearchForMyListing}
              onManualVerify={handleManualVerify}
              onCannotFind={handleCannotFindListing}
              actionsDisabled={running || pickerSubmitting || manualVerifying}
              showNotFoundState={showNotFoundState}
            />

            {/* 2. Highest-Impact Opportunity Detected — THE HOOK */}
            <DiagnosisHook result={result} />

            {/* 3. Current Google Maps position */}
            {/* RankTrustOS Intelligence — premium Gilliam Principle discovery */}
            <section className="bento overflow-hidden border border-zinc-200 bg-white" data-testid="opportunity-intelligence-summary">
              <div className="bg-zinc-950 text-white p-6 lg:p-8">
                <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#7FA6FF]">RankTrustOS Intelligence</div>
                <h2 className="font-display text-2xl lg:text-3xl font-black tracking-tight mt-2">See Your Business Through the Customer's Eyes</h2>
                <p className="text-sm lg:text-base text-white/70 mt-3 leading-relaxed max-w-3xl">Customers do not experience your business as a ranking, a review count or a website score. They move through a series of decisions: Can I find you? Do I trust you? Do you answer what I need to know? Why should I choose you? RankTrust connects the evidence across that journey.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="p-6 lg:p-7 border-b md:border-r border-zinc-200"><div className="text-[11px] font-mono uppercase tracking-wider text-[#2D5BFF]">Discovery & Visibility</div><h3 className="font-display text-xl font-black text-zinc-900 mt-2">Will customers encounter you early enough to consider you?</h3><p className="text-sm text-zinc-600 mt-3 leading-relaxed">RankTrust measures observable discovery across Google, Maps and AI-assisted search. Visibility matters because a business cannot earn trust or win the customer if it never enters the consideration set.</p><div className="mt-4 rounded-lg bg-zinc-50 border border-zinc-200 p-4"><div className="text-[10px] font-mono uppercase tracking-wider text-[#D4AF37]">Think about this</div><p className="text-sm font-semibold text-zinc-800 mt-1">Where are customers forming their shortlist before they ever encounter your business?</p></div></div>
                <div className="p-6 lg:p-7 border-b border-zinc-200"><div className="text-[11px] font-mono uppercase tracking-wider text-[#2D5BFF]">Trust & Customer Confidence</div><h3 className="font-display text-xl font-black text-zinc-900 mt-2">Once they find you, what makes choosing you feel safe?</h3><p className="text-sm text-zinc-600 mt-3 leading-relaxed">Reviews are only one signal. Customers also look for proof of expertise, licensing and credentials, guarantees, years in business, recognizable projects, clear pricing or expectations, professional presentation, consistent business information and evidence that you understand their problem.</p><div className="mt-4 rounded-lg bg-zinc-50 border border-zinc-200 p-4"><div className="text-[10px] font-mono uppercase tracking-wider text-[#D4AF37]">Think about this</div><p className="text-sm font-semibold text-zinc-800 mt-1">If your review count could not change tomorrow, what else could you show customers that would make them more confident choosing you?</p></div></div>
                <div className="p-6 lg:p-7 md:border-r border-b md:border-b-0 border-zinc-200"><div className="text-[11px] font-mono uppercase tracking-wider text-[#2D5BFF]">Website & Answer Intelligence</div><h3 className="font-display text-xl font-black text-zinc-900 mt-2">Does your website remove uncertainty — or leave customers with unanswered questions?</h3><p className="text-sm text-zinc-600 mt-3 leading-relaxed">Customers want to understand cost, process, timing, service area, qualifications, guarantees and what happens next. Clear answers can create confidence before a conversation begins and give search and answer engines better information to understand the business.</p><div className="mt-4 rounded-lg bg-zinc-50 border border-zinc-200 p-4"><div className="text-[10px] font-mono uppercase tracking-wider text-[#D4AF37]">Think about this</div><p className="text-sm font-semibold text-zinc-800 mt-1">What question would a serious customer need answered before feeling comfortable contacting you?</p></div></div>
                <div className="p-6 lg:p-7"><div className="text-[11px] font-mono uppercase tracking-wider text-[#2D5BFF]">Competitive Opportunity</div><h3 className="font-display text-xl font-black text-zinc-900 mt-2">What are competitors making easier to discover, understand or believe?</h3><p className="text-sm text-zinc-600 mt-3 leading-relaxed">The goal is not to copy competitors. It is to notice where they may be reducing uncertainty or creating confidence more effectively — then identify an opportunity your business can own.</p><div className="mt-4 rounded-lg bg-zinc-50 border border-zinc-200 p-4"><div className="text-[10px] font-mono uppercase tracking-wider text-[#D4AF37]">Think about this</div><p className="text-sm font-semibold text-zinc-800 mt-1">What might a customer learn or feel from a competitor that they cannot easily learn or feel from you today?</p></div></div>
              </div>
              <div className="m-6 lg:m-8 rounded-xl border border-[#D4AF37]/30 bg-[#FFFBF1] p-5 lg:p-6" data-testid="gilliam-discovery-question"><div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#A97800]">The Opportunity Beyond Public Data</div><h3 className="font-display text-xl lg:text-2xl font-black text-zinc-900 mt-2">Getting discovered is only the beginning. What happens after the customer responds?</h3><p className="text-sm text-zinc-600 mt-2 leading-relaxed">A public assessment can observe discovery, trust, website answers and competitive signals. It cannot responsibly see missed calls, slow lead response, estimates that go cold, customers who never return or manual processes that create friction. Those are not assumptions — they are high-value questions for deeper discovery.</p></div>
            </section>

            <section className="bento overflow-hidden border border-zinc-200 bg-white" data-testid="customer-confidence-framework">
              <div className="p-6 lg:p-8 border-b border-zinc-200">
                <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#2D5BFF]">Customer Confidence Framework</div>
                <h2 className="font-display text-2xl lg:text-3xl font-black tracking-tight text-zinc-900 mt-2">What Evidence Helps a Customer Feel Confident Choosing You?</h2>
                <p className="text-sm lg:text-base text-zinc-600 mt-3 leading-relaxed max-w-3xl">Reviews matter, but trust is larger than a star rating. Customers look for multiple forms of evidence before they decide a business feels credible, capable and safe to choose.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="p-6 border-b md:border-r border-zinc-200"><div className="label-tiny text-[#2D5BFF]">01 · Proof of Capability</div><h3 className="font-display text-lg font-black mt-2">Can customers see evidence that you can do the work well?</h3><p className="text-sm text-zinc-600 mt-2 leading-relaxed">Licenses, certifications, years of experience, project proof, specialties, warranties and clear service expertise can reduce uncertainty.</p><p className="text-sm font-semibold text-zinc-800 mt-4">Think about this: What proof would matter most to someone hiring you for the first time?</p></div>
                <div className="p-6 border-b border-zinc-200"><div className="label-tiny text-[#2D5BFF]">02 · Verified Local Presence</div><h3 className="font-display text-lg font-black mt-2">Does the business feel established, consistent and real?</h3><p className="text-sm text-zinc-600 mt-2 leading-relaxed">Consistent business identity, service-area clarity, Google Business Profile evidence and accurate contact information help customers verify who they are dealing with.</p><p className="text-sm font-semibold text-zinc-800 mt-4">Think about this: Could a customer verify your legitimacy without having to call you?</p></div>
                <div className="p-6 border-b md:border-b-0 md:border-r border-zinc-200"><div className="label-tiny text-[#2D5BFF]">03 · Reputation & Customer Assurance</div><h3 className="font-display text-lg font-black mt-2">What makes the decision feel lower-risk?</h3><p className="text-sm text-zinc-600 mt-2 leading-relaxed">Review quality and recency, testimonials, guarantees, transparent expectations, responsiveness and proof of satisfied customers work together to create confidence.</p><p className="text-sm font-semibold text-zinc-800 mt-4">Think about this: Beyond adding reviews, what could make choosing you feel safer?</p></div>
                <div className="p-6"><div className="label-tiny text-[#2D5BFF]">04 · People & Accountability</div><h3 className="font-display text-lg font-black mt-2">Who is standing behind the promise?</h3><p className="text-sm text-zinc-600 mt-2 leading-relaxed">Real team information, credentials, leadership visibility, professional standards and clear accountability can make a service business feel more trustworthy before the first conversation.</p><p className="text-sm font-semibold text-zinc-800 mt-4">Think about this: Does your digital presence show customers who they are trusting with their home or business?</p></div>
              </div>
              <div className="m-6 lg:m-8 rounded-xl bg-zinc-950 text-white p-5 lg:p-6"><div className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#D4AF37]">Digital Credibility Lens</div><p className="text-sm text-white/75 mt-2 leading-relaxed">RankTrust also looks for signals related to experience, expertise, authority and trustworthiness as a useful quality lens. These are not a single Google score or guaranteed ranking formula; they help reveal whether customers and answer engines have clear evidence to understand and trust the business.</p></div>
            </section>

            <GooglePositionCard result={result} />

            {/* 4. Observed Opportunity Signals — three contributing factors */}
            <ContributingFactorsBlock
              factors={result.contributing_factors}
              hasPositionAbove={!!(result.map_matched || result.serpapi_unavailable)}
            />

            {/* 5. Competitors */}
            <CompetitorsBlock top3={result.top3_competitors} businessName={result.business_name} />

            {/* 6. Website Analysis — Answer Visibility / AEO readiness */}
            <AnswerVisibilityBlock analysis={result.answer_visibility} />

            {/* 7. AI Search Visibility — observed outcome */}
            <AiVisibilityBlock result={result} />

            {/* 8. Overall Visibility Score — confirmation, not hook */}
            <OverallScoreBlock result={result} />

            {/* 8. 30/60/90-day Growth Plan */}
            <OpportunityActionHorizon result={result} />

            {/* 8.25 — LOCKED next step: strategy meeting → Personalized Growth Blueprint + complimentary Playbook */}
            <BlueprintMeetingCTA />

            {/* 8.5 SMS save/status utility */}
            <SmsBlock
              smsQueued={smsQueued}
              smsDeliveryStatus={smsDeliveryStatus}
              smsMocked={smsMocked}
              smsError={smsError}
              smsErrorCode={smsErrorCode}
              smsPhone={smsPhone}
              setSmsPhone={setSmsPhone}
              smsSending={smsSending}
              sendDiagnosisSms={sendDiagnosisSms}
              leadPhone={result.lead_phone}
            />

            {/* Full assessment already unlocked. Strategy meeting is the next conversion step. */}

            {/* Meta strip */}
            <div className="flex flex-wrap items-center justify-between gap-3 pt-4 text-sm text-zinc-500">
              <button
                className="font-mono hover:text-zinc-900 underline"
                onClick={() => { setResult(null); setEmail(""); setEmailSubmitted(false); }}
                data-testid="checker-run-another"
              >
                ← Run another check
              </button>
              <span className="font-mono text-xs">
                Check ID: {result.id.slice(0, 8)}
              </span>
            </div>

            {/* 10. Share tools */}
            <div className="bento p-6 lg:p-8 space-y-5" data-testid="checker-share">
              <div>
                <h2 className="font-display text-xl font-bold tracking-tight flex items-center gap-2 mb-1">
                  <Share size={18} weight="fill" /> Share your Business Growth Assessment
                </h2>
                <p className="text-zinc-600 text-sm">
                  Auto-generated card unfurls on LinkedIn, X, and iMessage.
                </p>
              </div>
              <div className="border border-zinc-200 overflow-hidden">
                <img
                  src={ogImageUrl}
                  alt={`${result.business_name} Business Growth Assessment share card`}
                  className="w-full h-auto block"
                  data-testid="checker-og-preview"
                />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <button
                  onClick={copyShareLink}
                  className="flex items-center justify-center gap-2 px-3 py-2.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-900 font-mono text-xs font-bold"
                  data-testid="checker-copy-link"
                >
                  <Copy size={14} weight="bold" /> Copy link
                </button>
                <a
                  href={ogImageUrl}
                  download={`${result.business_name.replace(/\s+/g, "-").toLowerCase()}-growth-diagnosis.png`}
                  className="flex items-center justify-center gap-2 px-3 py-2.5 bg-zinc-100 hover:bg-zinc-200 text-zinc-900 font-mono text-xs font-bold"
                  data-testid="checker-download-png"
                >
                  <DownloadSimple size={14} weight="bold" /> Save PNG
                </a>
                <a
                  href={twitterShareHref}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-2 px-3 py-2.5 bg-zinc-950 hover:bg-zinc-800 text-white font-mono text-xs font-bold"
                  data-testid="checker-share-x"
                >
                  Post to X
                </a>
                <a
                  href={linkedinShareHref}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-2 px-3 py-2.5 bg-[#0A66C2] hover:bg-[#084d92] text-white font-mono text-xs font-bold"
                  data-testid="checker-share-linkedin"
                >
                  Share on LinkedIn
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Footer methodology strip */}
        <div className="mt-16 pt-8 border-t border-zinc-200 text-xs text-zinc-500 font-mono leading-relaxed max-w-3xl">
          <strong className="text-zinc-700">How this diagnosis is built:</strong> RankTrust combines externally observable Google Maps, business profile, website, Answer Visibility, competitive and available AI-provider signals. AI results are shown only for providers actually measured; unavailable or not-yet-measured providers are never scored as zero. Website & Answer Intelligence incorporates both observable website health and the customer questions the analyzed page clearly answers. The Business Opportunity & Rank Signal is directional and is not a grade on the quality of the business. Recommendations are grounded in observed signals, while operational opportunities are informed by the information you provide and deeper discovery.
        </div>
      </main>

      {/* Phase 2 Slice 2A — GBP Candidate Picker modal */}
      {candidatePickerOpen && (
        <GbpCandidatePicker
          candidates={candidates}
          onSelect={handlePickerSelect}
          onNoneMatch={handlePickerNoneMatch}
          onCancel={handlePickerCancel}
          loading={pickerSubmitting}
        />
      )}

      {/* Phase 2 Slice 2E — Manual GBP verification modal */}
      {manualVerifyOpen && (
        <ManualVerificationModal
          onResolve={handleManualResolve}
          onCancel={() => setManualVerifyOpen(false)}
          loading={manualVerifying}
        />
      )}
    </div>
  );
}

// ---------- SMS block ----------

function SmsBlock({ smsQueued, smsDeliveryStatus, smsMocked, smsError, smsErrorCode, smsPhone, setSmsPhone, smsSending, sendDiagnosisSms, leadPhone }) {
  // Twilio invalid-number error codes — the ONLY case where we tell the visitor
  // that their number specifically is the problem. Every other failure code
  // (A2P 10DLC, carrier reject, auth, sender inactive, timeout, network) uses
  // the non-blaming "diagnosis is ready below" copy.
  const isInvalidNumber = smsErrorCode === 21211 || smsErrorCode === 21614;
  return (
    <section className="bento p-6 max-w-3xl" data-testid="sms-save-block">
      {smsQueued && smsDeliveryStatus === "delivered" ? (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="font-display font-bold text-emerald-700 flex items-center gap-2" data-testid="sms-sent-heading">
              <CheckCircle size={20} weight="fill" /> Your diagnosis has been delivered to your mobile device.
            </div>
            {smsMocked && (
              <div className="text-xs text-emerald-800/80 mt-1" data-testid="sms-sent-confirmation">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-bold uppercase tracking-wider">
                  Dev mode
                </span>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={() => sendDiagnosisSms(leadPhone || smsPhone)}
            disabled={smsSending || (!leadPhone && !smsPhone)}
            className="btn-secondary text-xs"
            data-testid="sms-send-again"
          >
            {smsSending ? "Sending…" : "Send Again"}
          </button>
        </div>
      ) : smsQueued && (smsDeliveryStatus === "failed" || smsDeliveryStatus === "undelivered") ? (
        <div>
          <div className="font-display font-bold text-zinc-900 flex items-center gap-2" data-testid="sms-failed-heading">
            <Warning size={18} weight="fill" className="text-amber-600" />
            {isInvalidNumber ? "That mobile number didn't work." : "Your diagnosis is ready below."}
          </div>
          <p className="text-sm text-zinc-700 mt-1" data-testid="sms-failed-message">
            {isInvalidNumber
              ? "Please verify your mobile number and try again."
              : "We were unable to deliver the text, but you can continue reviewing your results now."}
            {smsError && (
              <span className="block mt-1 text-zinc-500 font-mono text-xs">{smsError}</span>
            )}
          </p>
          <div className="mt-3 flex flex-col sm:flex-row gap-2 max-w-md">
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              className="input-field flex-1"
              placeholder="(817) 555-1234"
              value={smsPhone}
              onChange={(e) => setSmsPhone(e.target.value)}
              data-testid="sms-phone-input"
            />
            <button
              type="button"
              onClick={() => sendDiagnosisSms()}
              disabled={smsSending}
              className="btn-primary text-sm whitespace-nowrap"
              data-testid="sms-send-btn"
            >
              {smsSending ? "Sending…" : "Try Again"}
            </button>
          </div>
        </div>
      ) : smsQueued ? (
        <div className="flex items-center gap-3" data-testid="sms-sending-block">
          <div className="w-5 h-5 rounded-full border-2 border-blue-300 border-t-blue-600 animate-spin" />
          <div>
            <div className="font-display font-bold text-blue-800" data-testid="sms-sending-heading">
              Your text is being sent.
            </div>
            <p className="text-xs text-blue-800/70 mt-0.5">
              We'll confirm delivery in a moment.
            </p>
          </div>
        </div>
      ) : (
        <div>
          <div className="font-display font-bold text-zinc-900" data-testid="sms-save-prompt">
            Want to save this diagnosis so you can return anytime?
          </div>
          <p className="text-xs text-zinc-500 mt-1">
            We'll text you a secure link to this exact report.
          </p>
          <div className="mt-3 flex flex-col sm:flex-row gap-2 max-w-md">
            <input
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              className="input-field flex-1"
              placeholder="(817) 555-1234"
              value={smsPhone}
              onChange={(e) => setSmsPhone(e.target.value)}
              data-testid="sms-phone-input"
            />
            <button
              type="button"
              onClick={() => sendDiagnosisSms()}
              disabled={smsSending}
              className="btn-primary text-sm whitespace-nowrap"
              data-testid="sms-send-btn"
            >
              {smsSending ? "Sending…" : "Text My Diagnosis"}
            </button>
          </div>
          {smsError && (
            <div className="mt-2 text-xs text-red-700" data-testid="sms-error">{smsError}</div>
          )}
        </div>
      )}
    </section>
  );
}


